import React, { useState, useEffect, createContext, useContext } from 'react';
import { Lock, Eye, EyeOff, Users, LogOut, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import Header from './Header';
import Footer from './Footer';

const ADMIN_PASSWORD = 'Laikos24@!';
const TEACHER_PASSWORD = 'Laikos24@!T';
const STORAGE_KEY = 'laikos_admin_authenticated';
const SESSION_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds
const REMEMBER_DURATION = 60 * 24 * 60 * 60 * 1000; // 60 days in milliseconds

// Create context for access type
export const AccessTypeContext = createContext({ isTeacherAccess: false, isAdminAccess: false, forgetDevice: () => {} });

export const useAccessType = () => useContext(AccessTypeContext);

const AdminProtected = ({ children, title = "Protected Content" }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isTeacherAccess, setIsTeacherAccess] = useState(false);
  const [isRemembered, setIsRemembered] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(0);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated
    const authData = localStorage.getItem(STORAGE_KEY);
    if (authData) {
      try {
        const { timestamp, accessType, duration } = JSON.parse(authData);
        const now = new Date().getTime();
        const sessionDuration = duration || SESSION_DURATION;
        if (now - timestamp < sessionDuration) {
          setIsAuthenticated(true);
          setIsTeacherAccess(accessType === 'teacher');
          // Check if this is a remembered session (60 days)
          if (duration === REMEMBER_DURATION) {
            setIsRemembered(true);
            const remaining = Math.ceil((sessionDuration - (now - timestamp)) / (24 * 60 * 60 * 1000));
            setDaysRemaining(remaining);
          }
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    setLoading(false);
  }, []);

  const forgetDevice = () => {
    localStorage.removeItem(STORAGE_KEY);
    setIsAuthenticated(false);
    setIsRemembered(false);
    setIsTeacherAccess(false);
    setPassword('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const sessionDuration = rememberDevice ? REMEMBER_DURATION : SESSION_DURATION;
    
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setIsTeacherAccess(false);
      setError('');
      if (rememberDevice) {
        setIsRemembered(true);
        setDaysRemaining(60);
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        timestamp: new Date().getTime(),
        accessType: 'admin',
        duration: sessionDuration
      }));
    } else if (password === TEACHER_PASSWORD) {
      setIsAuthenticated(true);
      setIsTeacherAccess(true);
      setError('');
      if (rememberDevice) {
        setIsRemembered(true);
        setDaysRemaining(60);
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        timestamp: new Date().getTime(),
        accessType: 'teacher',
        duration: sessionDuration
      }));
    } else {
      setError('Incorrect password. Please try again.');
      setPassword('');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5f7c8a]"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-1 pt-14 flex items-center justify-center px-4">
          <Card className="w-full max-w-md shadow-lg">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-[#5f7c8a] rounded-full flex items-center justify-center mb-4">
                <Lock className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-xl font-semibold text-gray-800">
                Access Required
              </CardTitle>
              <p className="text-sm text-gray-500 mt-2">
                {title} is protected. Enter your password to continue.
              </p>
              <div className="mt-3 flex justify-center gap-4 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <Lock size={12} /> Admin
                </span>
                <span className="flex items-center gap-1">
                  <Users size={12} /> Teacher
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError('');
                    }}
                    placeholder="Enter password"
                    className="pr-10"
                    autoFocus
                    data-testid="access-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="rememberDevice"
                    checked={rememberDevice}
                    onCheckedChange={setRememberDevice}
                    data-testid="remember-device-checkbox"
                  />
                  <label 
                    htmlFor="rememberDevice" 
                    className="text-sm text-gray-600 cursor-pointer select-none"
                  >
                    Remember this device for 60 days
                  </label>
                </div>
                
                {error && (
                  <p className="text-red-500 text-sm text-center">{error}</p>
                )}
                
                <Button 
                  type="submit" 
                  className="w-full bg-[#5f7c8a] hover:bg-[#4a6470]"
                  disabled={!password}
                  data-testid="access-content-btn"
                >
                  Access Content
                </Button>
              </form>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <AccessTypeContext.Provider value={{ isTeacherAccess, isAdminAccess: !isTeacherAccess, forgetDevice, isRemembered, daysRemaining }}>
      {isRemembered && (
        <div className="fixed bottom-4 right-4 z-50">
          <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock size={16} className="text-[#5f7c8a]" />
              <span>Device remembered ({daysRemaining} days left)</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={forgetDevice}
              className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 px-2"
              data-testid="forget-device-btn"
            >
              <LogOut size={14} className="mr-1" />
              Forget
            </Button>
          </div>
        </div>
      )}
      {children}
    </AccessTypeContext.Provider>
  );
};

export default AdminProtected;
